// Inventory: Changes (Starchive-Inventory-Section-Plan, section 3.6; the canvas's I7 board). What left the floor since the
// stock record, with the sale that explains it or "no sale" in red; what came on; price, owner and status changes from the
// live feed; the stock record by night. This is the tab that answers "a piece is missing from the rack, what happened".
import { useState } from 'react'
import { Screen } from '../ReportsApp'
import { useReport } from '../api'
import { dayShort, int, money, moneyTile, n, whenShort } from '../format'
import { Badge, Card, Note, Tile } from '../ui'
import { OwnerChip, PhotoImg } from './inventory-ui'
import { PieceDrawer } from './PieceDrawer'

interface Gone { product_id: string; title: string; brand: string; image_url: string | null; owner: string | null; consignor_key: string | null; list_price: number; seen: string; deleted: boolean; sale: string | null; paid: number | null }
interface ChangesData {
  days: number; snapshot: string; today: string
  tiles: { gone_no_sale: number; gone_no_sale_list: number; gone_with_sale: number; new_listings: number; on_floor_now: number }
  gone: Gone[]
  new_listings: { product_id: string; title: string; image_url: string | null; list_price: number; on_floor_since: string }[]
  new_more: number
  other: { at: string; kind: string; title: string; product_id: string; before: unknown; after: unknown; source: string }[]
  stock: { date: string; pieces: number; list_value: number }[]
}

const kindLine = (e: ChangesData['other'][number]) => {
  const b = e.before, a = e.after
  switch (e.kind) {
    case 'price': return `Price ${money(n(b) / 100, true)} to ${money(n(a) / 100, true)}`
    case 'cost': return `Shopify cost ${b == null ? 'blank' : money(n(b) / 100, true)} to ${a == null ? 'blank' : money(n(a) / 100, true)}`
    case 'owner': return `Owner tag ${String(b ?? 'none')} to ${String(a ?? 'none')}`
    case 'status': return `Status ${String(b)} to ${String(a)}`
    case 'title': return 'Title changed'
    case 'deleted': return 'Listing deleted in Shopify'
    case 'restored': return 'Listing came back'
    case 'variant_added': return 'A size was added'
    case 'variant_removed': return 'A size was removed'
    default: return String(e.kind)
  }
}

export function Changes() {
  const [days, setDays] = useState(1)
  const { data: d, error, loading } = useReport<ChangesData>('inv_changes', { days }, [days])
  const [open, setOpen] = useState<string | null>(null)
  const t = d?.tiles
  const lo = Math.min(...(d?.stock ?? [{ pieces: 0 }]).map((s) => s.pieces)) - 30
  const hi = Math.max(...(d?.stock ?? [{ pieces: 1 }]).map((s) => s.pieces))

  return (
    <Screen title="Changes" sub="What left the floor, what came on, and anything that vanished without a sale. Live from the store's feed" wide
      right={<div className="rp-vseg" role="group" aria-label="Window">
        {([[1, 'Since the record'], [7, '7 days'], [30, '30 days']] as const).map(([v, l]) => <button key={v} type="button" aria-pressed={days === v} onClick={() => setDays(v)}>{l}</button>)}
      </div>}>
      {error && <div className="rp-error">{error}</div>}
      <div className="rp-grid rp-grid--tiles">
        <Tile label="Gone, no sale yet" tone={t && t.gone_no_sale ? 'red' : 'plain'} value={t ? int(t.gone_no_sale) : '…'} sub={t ? `${moneyTile(t.gone_no_sale_list)} at list · left the floor since the ${dayShort(d!.snapshot)} stock record with no sale in the ledger` : ''} />
        <Tile label="Gone with a sale" tone="green" value={t ? int(t.gone_with_sale) : '…'} sub="Each matched to its sale by the listing" />
        <Tile label={`New listings, ${days === 1 ? 'this week' : `${days} days`}`} value={t ? int(t.new_listings) : '…'} sub={t ? `${int(t.on_floor_now)} pieces on the floor now` : ''} />
        <div className="rp-tile">
          <div className="rp-tile__head"><span>Stock by night</span><b style={{ fontSize: 13 }}>{d?.stock.at(-1)?.pieces ?? '…'}</b></div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end', paddingTop: 4 }}>
            {(d?.stock ?? []).map((s2, i) => (
              <div key={s2.date} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, flexGrow: 1 }} title={`${dayShort(s2.date)}: ${int(s2.pieces)} pieces, ${moneyTile(s2.list_value)}`}>
                <div style={{ width: '100%', height: 40, display: 'flex', alignItems: 'flex-end' }}>
                  <div style={{ width: '100%', height: Math.max(3, Math.round((s2.pieces - lo) / Math.max(1, hi - lo) * 40)), borderRadius: 3, background: i === (d?.stock.length ?? 0) - 1 ? '#1c1c1e' : '#c7c7cc' }} />
                </div>
                <span style={{ fontSize: 10, color: '#6c6c70' }}>{s2.date.slice(8)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="rp-grid rp-grid--2" style={{ alignItems: 'start' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <Card title="Gone since the stock record" tone={t && t.gone_no_sale ? 'red' : 'green'}
            note="Often a piece marked out of stock at the moment of sale and rung later: the row clears itself when the sale is rung. A red row with no sale by the evening is worth asking about.">
            <div className="rp-table" role="table">
              <div className="rp-table__head" style={{ gridTemplateColumns: 'minmax(0, 1.8fr) 120px 70px minmax(0, 1.6fr)' }} role="row">
                {['Piece', 'Whose', 'List', 'What happened'].map((h, i) => <span key={h} role="columnheader" className={i === 2 ? 'r' : ''}>{h}</span>)}
              </div>
              {(d?.gone ?? []).map((g) => (
                <div key={g.product_id} className="rp-table__row" style={{ gridTemplateColumns: 'minmax(0, 1.8fr) 120px 70px minmax(0, 1.6fr)' }} role="row">
                  <span role="cell" className="rp-pcell" style={{ cursor: 'pointer' }} onClick={() => setOpen(g.product_id)}><PhotoImg url={g.image_url} title={g.title} width={96} small /><span className="rp-pcell__text"><b>{g.title}</b><span className="rp-sub">{g.brand}</span></span></span>
                  <span role="cell"><OwnerChip owner={g.owner} consignorKey={g.consignor_key} /></span>
                  <span role="cell" className="r">{money(g.list_price, true)}</span>
                  <span role="cell">{g.sale
                    ? <Badge tone="green">Sold · {g.sale}{g.paid != null ? ` · ${money(g.paid, true)}` : ''}</Badge>
                    : <span className="wrap" style={{ fontSize: 12, color: '#a4000f', whiteSpace: 'normal' }}>{g.seen}. No sale in the ledger.</span>}</span>
                </div>
              ))}
              {!loading && (d?.gone ?? []).length === 0 && <div className="rp-table__empty">Nothing has left the floor since the stock record</div>}
            </div>
          </Card>
          <Card title="Other changes" tone="grey" note="Price, owner, cost and status changes the feed has seen (live from today; history builds from here)">
            <div className="rp-table" role="table">
              <div className="rp-table__head" style={{ gridTemplateColumns: '130px minmax(0, 1.6fr) minmax(0, 1.4fr)' }} role="row">
                {['When', 'Piece', 'What changed'].map((h) => <span key={h} role="columnheader">{h}</span>)}
              </div>
              {(d?.other ?? []).map((e, i) => (
                <div key={i} className="rp-table__row" style={{ gridTemplateColumns: '130px minmax(0, 1.6fr) minmax(0, 1.4fr)' }} role="row">
                  <span role="cell" className="rp-sub">{d ? whenShort(e.at, d.today) : ''}</span>
                  <span role="cell" style={{ cursor: 'pointer', fontSize: 13, fontWeight: 600 }} onClick={() => setOpen(e.product_id)}>{e.title}</span>
                  <span role="cell" className="wrap" style={{ fontSize: 13, whiteSpace: 'normal' }}>{kindLine(e)}{e.source === 'pull' ? ' (nightly)' : ''}</span>
                </div>
              ))}
              {!loading && (d?.other ?? []).length === 0 && <div className="rp-table__empty">No price, owner or status changes in this window yet. The feed records them from today on.</div>}
            </div>
          </Card>
        </div>
        <Card title="New listings" tone="grey" note={d ? `On the floor now, listed in the last ${days === 1 ? 7 : days} days` : ''}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 10, padding: '4px 20px 12px' }}>
            {(d?.new_listings ?? []).map((p) => (
              <div key={p.product_id} style={{ display: 'flex', flexDirection: 'column', gap: 4, cursor: 'pointer', minWidth: 0 }} onClick={() => setOpen(p.product_id)}>
                <PhotoImg url={p.image_url} title={p.title} width={220} />
                <span className="rp-sub" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{money(p.list_price, true)} · {dayShort(p.on_floor_since)}</span>
              </div>
            ))}
          </div>
          {(d?.new_more ?? 0) > 0 && <Note>And {int(d!.new_more)} more; the On the floor tab sorted by Recently listed has them all.</Note>}
        </Card>
      </div>
      {open && <PieceDrawer productId={open} onClose={() => setOpen(null)} /> }
    </Screen>
  )
}
